/**
 * Several media plans picked together (Combine): the bundle's numbers and where each plan's agreement stands.
 * Money is summed in one currency only — a mixed bundle says so instead of adding SEK to EUR.
 */
import type { MediaPlan } from '@/cowork/omniad/api/coworkApi';

export type AgreementState = { kind: 'bundle'; url: string; shared: number } | { kind: 'own'; url: string } | { kind: 'none' };
export interface BundleRow { plan: MediaPlan; start: string; end: string; spend: number; net: number; takeRate: number | null; agreement: AgreementState }
export interface BundleSummary {
  currency: string | null; mixedCurrency: string[];
  /** Plans for more than one client (brand or client name): they cannot share an agreement. */
  mixedClients: string[];
  investment: number; spend: number; net: number; takeRate: number | null;
  creators: number; impressions: number; beats: number; start: string; end: string; markets: string[];
  rows: BundleRow[];
  /** One Oneflow agreement already covers every picked plan. */
  sharedAgreement: { url: string; id: string } | null;
  agreements: { bundle: number; own: number; none: number };
}

const isDate = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);

export function bundleSummary(plans: MediaPlan[]): BundleSummary {
  const currencies = [...new Set(plans.map((p) => p.currency))];
  const byAgreement = new Map<string, number>();
  for (const p of plans) if (p.agreement?.id) byAgreement.set(p.agreement.id, (byAgreement.get(p.agreement.id) || 0) + 1);
  const rows: BundleRow[] = plans.map((p) => {
    const dates = p.beats.flatMap((b) => [b.dateDrafts, b.dateDeliver]).filter(isDate).sort();
    const spend = p.beats.reduce((a, b) => a + (b.budget || 0), 0);
    const net = p.totalBudget - spend;
    const shared = p.agreement?.id ? byAgreement.get(p.agreement.id) || 1 : 0;
    return {
      plan: p, start: dates[0] || '', end: dates[dates.length - 1] || '', spend, net,
      takeRate: p.totalBudget && spend ? Math.round((net / p.totalBudget) * 1000) / 10 : null,
      agreement: !p.agreement?.id ? { kind: 'none' } : shared > 1 ? { kind: 'bundle', url: p.agreement.url, shared } : { kind: 'own', url: p.agreement.url },
    };
  });
  const investment = plans.reduce((a, p) => a + (p.totalBudget || 0), 0);
  const spend = rows.reduce((a, r) => a + r.spend, 0);
  const starts = rows.map((r) => r.start).filter(Boolean).sort(); const ends = rows.map((r) => r.end).filter(Boolean).sort();
  const firstAgreement = plans[0]?.agreement;
  const brands = new Set(plans.map((p) => p.brandId).filter(Boolean));
  const clients = [...new Map(plans.map((p) => String(p.clientName || '').trim()).filter(Boolean).map((c) => [c.toLowerCase().replace(/\s+(ab|as|a\/s|oy|gmbh|ltd|inc)\.?$/i, ''), c])).values()];
  return {
    currency: currencies.length === 1 ? currencies[0] : null, mixedCurrency: currencies.length > 1 ? currencies : [],
    mixedClients: brands.size > 1 || clients.length > 1 ? (clients.length > 1 ? clients : ['more than one brand']) : [],
    investment, spend, net: investment - spend, takeRate: investment && spend ? Math.round(((investment - spend) / investment) * 1000) / 10 : null,
    creators: plans.reduce((a, p) => a + (p.totals?.creators || 0), 0), impressions: plans.reduce((a, p) => a + (p.totals?.impressions || 0), 0),
    beats: plans.reduce((a, p) => a + p.beats.length, 0), start: starts[0] || '', end: ends[ends.length - 1] || '',
    markets: [...new Set(plans.flatMap((p) => String(p.market || '').split(/[+,\s]+/)).map((m) => m.trim().toUpperCase()).filter(Boolean))],
    rows,
    sharedAgreement: firstAgreement?.id && plans.every((p) => p.agreement?.id === firstAgreement.id) ? { id: firstAgreement.id, url: firstAgreement.url } : null,
    agreements: { bundle: rows.filter((r) => r.agreement.kind === 'bundle').length, own: rows.filter((r) => r.agreement.kind === 'own').length, none: rows.filter((r) => r.agreement.kind === 'none').length },
  };
}
